import React, { useCallback, useMemo, useState, createContext, useContext, memo } from 'react';
/**
 * Lightweight, dependency-free navigation.
 *
 * We intentionally avoid react-router here: in the sandboxed preview iframe its
 * history backend (@remix-run/router/history) throws, and mounting one Router
 * inside another (the gallery) is unsupported. This context keeps a per-frame
 * navigation stack entirely in memory and exposes a small, react-router-shaped
 * API (useNavigate / useLocation) so screen components barely change.
 */
type NavContextValue = {
  pathname: string;
  push: (to: string) => void;
  back: () => void;
};
const NavContext = createContext<NavContextValue | null>(null);
export function ScreenRouter({
  initialPath = '/',
  children
}: {
  initialPath?: string;
  children: React.ReactNode;
}) {
  const [stack, setStack] = useState<string[]>([initialPath || '/']);
  const push = useCallback((to: string) => {
    setStack(prev => [...prev, to]);
  }, []);
  const back = useCallback(() => {
    setStack(prev => prev.length > 1 ? prev.slice(0, -1) : prev);
  }, []);
  const value = useMemo<NavContextValue>(() => ({
    pathname: stack[stack.length - 1],
    push,
    back
  }), [stack, push, back]);
  return <NavContext.Provider value={value} data-id="element-165" data-mp="3lo5oi7b37zis">{children}</NavContext.Provider>;
}
function useNav(): NavContextValue {
  const ctx = useContext(NavContext);
  if (!ctx) {
    throw new Error('Navigation hooks must be used within a <ScreenRouter>');
  }
  return ctx;
}
/**
 * react-router-compatible navigate(): accepts a path string to push, or -1 to
 * go back. Other numeric deltas collapse to a single back step (sufficient for
 * this prototype's flows).
 */
export function useNavigate() {
  const {
    push,
    back
  } = useNav();
  return useCallback((to: string | number) => {
    if (typeof to === 'number') {
      if (to < 0) back();
      return;
    }
    push(to);
  }, [push, back]);
}
/** react-router-compatible useLocation(): exposes the current pathname. */
export function useLocation() {
  const {
    pathname: fullPath
  } = useNav();
  const [pathname, search] = fullPath.split('?');
  return {
    pathname,
    search: search ? `?${search}` : ''
  };
}